import React from 'react';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  const baseUrl =
    process.env.REACT_APP_IMAGE_BASE_URL ||
    'https://api.soksan-dss.com/uploads/crop/VariantFeatureImages/';
  return `${baseUrl}/${imagePath}`;
};

const getFallbackIcon = () => {
  return 'Plant';
};

const colorPalette = [
  '#1B5E20',
  '#2E7D32',
  '#388E3C',
  '#43A047',
  '#4CAF50',
  '#66BB6A',
  '#81C784',
  '#A5D6A7',
  '#C8E6C9',
  '#E8F5E9',
];

const VariantItem = ({
  name,
  imageUrl,
  fallbackIcon,
  value,
  barColor,
  maxValue,
  imageError,
  onImageError,
}) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center">
        <div
          style={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={name}
              style={{
                width: '24px',
                height: '24px',
                objectFit: 'contain',
              }}
              onError={onImageError}
            />
          ) : (
            <span style={{ fontSize: '12px' }}>{fallbackIcon}</span>
          )}
        </div>

        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
            <span
              className="fw-medium text-truncate"
              style={{
                fontSize: '14px',
              }}
            >
              {name}
            </span>
            <span
              className="fw-bold"
              style={{
                color: barColor,
                fontSize: '14px',
                marginLeft: '8px',
                flexShrink: 0,
              }}
            >
              {value}
            </span>
          </div>

          <div
            style={{
              height: '6px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${percentage}%`,
                height: '100%',
                backgroundColor: barColor,
                borderRadius: '4px',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const TopVariants = ({ topVariants = [] }) => {
  const [imageErrors, setImageErrors] = React.useState({});

  const processedVariants = React.useMemo(() => {
    if (!topVariants || topVariants.length === 0) {
      return [];
    }

    const sortedVariants = [...topVariants]
      .filter((variant) => variant.Count > 0)
      .sort((a, b) => b.Count - a.Count)
      .slice(0, 8);

    const maxCount = sortedVariants.length > 0 ? sortedVariants[0].Count : 1;

    return sortedVariants.map((variant, index) => {
      const variantId = variant.VariantID || variant.id || `variant-${index}`;
      const variantName = variant.VariantName || variant.name || 'Unknown Variant';
      const imageUrl = getImageUrl(variant.VariantImage || variant.image);
      const fallbackIcon = getFallbackIcon(variantName);
      const count = variant.Count || variant.value || 0;
      const barColor = colorPalette[index % colorPalette.length];

      return {
        id: variantId,
        rank: index + 1,
        name: variantName,
        cropName: variant.CropName,
        imageUrl,
        fallbackIcon,
        value: count,
        barColor,
        maxValue: maxCount,
      };
    });
  }, [topVariants]);

  const handleImageError = (variantId) => {
    setImageErrors((prev) => ({
      ...prev,
      [variantId]: true,
    }));
  };

  if (processedVariants.length === 0) {
    return (
      <div className="col-12 col-xl-6">
        <div className="card h-100">
          <div className="card-body">
            <h6 className="text-lg mb-4" style={{ color: '#1B5E20', fontWeight: 'bold' }}>
              Top Crop Varieties
            </h6>
            <div className="text-center py-4">
              <div className="mb-3" style={{ fontSize: '24px', opacity: 0.3 }}>
                Plant
              </div>
              <p className="text-muted mb-0">No variety data available</p>
              <small className="text-muted">Select a different date range</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 col-xl-6">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="text-lg mb-0" style={{ color: '#1B5E20', fontWeight: 'bold' }}>
              Top Crop Varieties
            </h6>
          </div>

          <div className="d-flex flex-column">
            {processedVariants.slice(0, 5).map((variant) => (
              <VariantItem
                key={variant.id}
                name={variant.name}
                imageUrl={variant.imageUrl}
                fallbackIcon={variant.fallbackIcon}
                value={variant.value}
                barColor={variant.barColor}
                maxValue={variant.maxValue}
                imageError={imageErrors[variant.id]}
                onImageError={() => handleImageError(variant.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

TopVariants.defaultProps = {
  topVariants: [],
};

export default TopVariants;
